import { ADMISSION_PREFILL_PREFIX, AUTOFILL_OWNER } from "./mappingConstants";

/** The other signer role a prefill-linked field can carry (mirrors the backend's
 * `DocumentSignerRole.ADMIN_PRE_SEND`; see `DocumentPrefillService.findMissingAutofilledFields`,
 * which treats both roles the same once a field has a `prefillKey`). */
const ADMIN_PRE_SEND_OWNER = "ADMIN_PRE_SEND";

/** True for a field mapped to the linked admission: the one prefill source that never blocks a
 * send when it resolves to nothing (see `AdmissionPrefillResolver.blocksSendWhenMissing`), so a
 * blank value here has to be caught here instead of relying on the backend's 400. */
export const isAdmissionAutofillField = (field) =>
  Boolean(field?.prefillKey?.startsWith(ADMISSION_PREFILL_PREFIX)) &&
  (field.signerRole === AUTOFILL_OWNER || field.signerRole === ADMIN_PRE_SEND_OWNER);

/**
 * Pulls the file name and missing field labels out of `CollegeDocumentSendService`'s blocking-gap
 * message (`"File.pdf" is missing: Label one, Label two`). Returns null for any other message,
 * including the generic fallback text, so a send failure that isn't this specific shape never
 * fabricates fields to fill in.
 */
export const parseMissingFieldsError = (message) => {
  if (typeof message !== "string") {
    return null;
  }
  const match = message.match(/^"(.+)" is missing: (.+)$/);
  if (!match) {
    return null;
  }
  const [, fileName, labelList] = match;
  return labelList.trim() ? { fileName, labelList: labelList.trim() } : null;
};

/**
 * Splits the backend's joined label list back into labels. A naive split on "," breaks any label
 * that contains one ("Passport number, page 1"), producing fragments that match no field, so the
 * typed override never lands and the send stays blocked on every retry. Known labels are matched
 * whole first, longest first so a label that contains a shorter one cannot be shadowed; whatever
 * is left over falls back to a plain split.
 */
export const splitMissingLabels = (labelList, knownLabels = []) => {
  let remaining = labelList;
  const found = [];
  [...knownLabels]
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .forEach((label) => {
      const at = remaining.indexOf(label);
      if (at === -1) {
        return;
      }
      const before = remaining.slice(0, at);
      const after = remaining.slice(at + label.length);
      const boundedStart = before === "" || before.endsWith(", ");
      const boundedEnd = after === "" || after.startsWith(",");
      if (!boundedStart || !boundedEnd) {
        return;
      }
      found.push(label);
      remaining = `${before}${after.replace(/^,\s*/, "")}`.replace(/,\s*$/, "");
    });

  const leftovers = remaining
    .split(",")
    .map((label) => label.trim())
    .filter(Boolean);
  return [...found, ...leftovers];
};

/**
 * Every prefill-linked field the admin needs an input for before sending: fields the backend's
 * last send attempt refused to accept blank (`blockingGap`, marked `blocking: true`), plus
 * admission-linked fields that would otherwise reach the client blank because no admission is
 * picked (the backend never rejects those, so they can only be caught here, marked
 * `blocking: false`). Deduplicated by file and field name; reads only already-loaded mapping data,
 * it fetches nothing itself.
 */
export const collectPendingOverrideFields = ({
  selectedFileIds,
  admissionId,
  blockingGap,
  fieldsByFileId,
  fileNameById,
}) => {
  const collected = new Map();

  if (!admissionId) {
    selectedFileIds.forEach((fileId) => {
      (fieldsByFileId[fileId] || []).filter(isAdmissionAutofillField).forEach((field) => {
        collected.set(`${fileId}:${field.name}`, {
          fileId,
          fileName: fileNameById[fileId] || "",
          fieldName: field.name,
          label: field.label,
          blocking: false,
        });
      });
    });
  }

  if (blockingGap && selectedFileIds.includes(blockingGap.fileId)) {
    const fields = fieldsByFileId[blockingGap.fileId] || [];
    const labels = splitMissingLabels(
      blockingGap.labelList,
      fields.map((field) => field.label)
    );
    labels.forEach((label) => {
      // The blocking-gap message only names a field's label, never its name, so the merged
      // mapping data is used to translate back to the name the `overrides` map is keyed by. Falls
      // back to the label itself on a cache miss (mapping not loaded yet): the retry keeps
      // reporting the same gap until the mapping query resolves and the real name is known.
      const matchedField = fields.find((field) => field.label === label);
      const fieldName = matchedField ? matchedField.name : label;
      collected.set(`${blockingGap.fileId}:${fieldName}`, {
        fileId: blockingGap.fileId,
        fileName: blockingGap.fileName,
        fieldName,
        label,
        blocking: true,
      });
    });
  }

  return Array.from(collected.values());
};

/**
 * Strips blank entries so a field the admin never touched is simply absent from the request
 * rather than sent as an empty-string override (which would still win over any resolved prefill
 * value, per `CollegeDocumentSendService.resolveValues` applying overrides last).
 */
export const buildOverridesPayload = (overridesByFile, selectedFileIds) => {
  const payload = {};
  selectedFileIds.forEach((fileId) => {
    const fields = overridesByFile[fileId];
    if (!fields) {
      return;
    }
    const nonBlank = Object.fromEntries(
      Object.entries(fields).filter(([, value]) => Boolean(value && value.trim().length > 0))
    );
    if (Object.keys(nonBlank).length > 0) {
      payload[fileId] = nonBlank;
    }
  });
  return payload;
};
